import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import styled from 'styled-components';
import { FiCheckCircle, FiFileText, FiTarget, FiAlertCircle } from 'react-icons/fi';

const StyledModal = styled(Modal)`
  .modal-content {
    background-color: #020617;
    color: #f1f5f9;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 2.5rem;
    overflow: hidden;
    box-shadow: 0 50px 100px -20px rgba(0, 0, 0, 0.8);
  }
  .modal-header {
    border: none;
    padding: 3rem 3rem 1rem;
    position: relative;
    background: linear-gradient(180deg, rgba(168, 85, 247, 0.05) 0%, transparent 100%);
    
    .btn-close {
      position: absolute;
      top: 2rem;
      right: 2rem;
      filter: invert(1) grayscale(100%) brightness(200%);
      z-index: 10;
    }
  }
  .modal-body {
    padding: 1rem 3rem 3rem;
    max-height: 70vh;
    overflow-y: auto;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: rgba(167, 139, 250, 0.7) rgba(255, 255, 255, 0.025);

    &::-webkit-scrollbar {
      width: 11px;
    }

    &::-webkit-scrollbar-track {
      margin-block: 10px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.025);
    }

    &::-webkit-scrollbar-thumb {
      min-height: 64px;
      border: 3px solid #020617;
      border-radius: 999px;
      background: linear-gradient(
        180deg,
        rgba(196, 181, 253, 0.9),
        rgba(139, 92, 246, 0.58)
      );
      background-clip: padding-box;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(
        180deg,
        rgba(216, 206, 255, 0.98),
        rgba(139, 92, 246, 0.78)
      );
      background-clip: padding-box;
    }
  }

  @media (max-width: 575.98px) {
    .modal-body {
      scrollbar-gutter: auto;

      &::-webkit-scrollbar {
        width: 9px;
      }
    }
  }
  .modal-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    padding: 1.5rem 3rem;
    background: rgba(255, 255, 255, 0.02);
  }
`;

const SectionBox = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 1.5rem;
  padding: 2rem;
  margin-bottom: 1.5rem;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 800;
    color: #a78bfa;
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  p, li {
    font-size: 0.95rem;
    color: #cbd5e1;
    line-height: 1.7;
    margin-bottom: 0.75rem;
    word-break: keep-all;
  }
  
  strong {
    color: #fff;
    font-weight: 700;
  }

  ol {
    padding-left: 1.25rem;
    li {
        margin-bottom: 0.75rem;
        &::marker {
            color: #a78bfa;
            font-weight: 800;
        }
    }
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    width: fit-content;
    padding: 0.4rem 0.8rem;
    background: rgba(168, 85, 247, 0.1);
    border: 1px solid rgba(168, 85, 247, 0.2);
    border-radius: 999px;
    color: #a78bfa;
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  h2 {
    font-size: 2rem;
    font-weight: 900;
    color: #fff;
    margin: 0;
  }
`;

const TermsModal = ({ show, onHide }) => {
  return (
    <StyledModal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <HeaderTitle>
            <div className="badge"><FiFileText /> Service Terms</div>
            <h2>서비스 이용약관</h2>
        </HeaderTitle>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-5 text-slate-400 font-medium leading-relaxed">
            나니아랩 서비스를 이용해주셔서 감사합니다. <br />
            본 약관은 원활한 교육 서비스 이용을 위해 회사와 회원 간의 권리와 의무 사항을 담고 있습니다.
        </p>

        <SectionBox>
            <h3><FiTarget /> 제1조 (목적 및 정의)</h3>
            <p>본 약관은 회사가 제공하는 <strong>나니아 터미널</strong> 및 AI 교육 콘텐츠를 이용함에 있어 필요한 제반 사항을 규정합니다.</p>
            <ol>
                <li>"나니아 터미널"이란 이용자가 웹 브라우저를 통해 AI를 학습시키고 코딩할 수 있도록 제공되는 지능형 플랫폼입니다.</li>
                <li>"이용자"란 회사의 서비스에 접속하여 본 약관에 따라 혜택을 받는 모든 회원을 의미합니다.</li>
            </ol>
        </SectionBox>

        <SectionBox>
            <h3><FiCheckCircle /> 제2조 (서비스의 제공)</h3>
            <p>회사는 이용자에게 다음과 같은 전문 교육 서비스를 제공합니다.</p>
            <ul>
                <li>실전 텍스트 코딩 기반의 프로젝트 학습 환경</li>
                <li>AI 튜터를 활용한 실시간 코드 리뷰 및 멘토링</li>
                <li>개인별 프로젝트 결과물의 웹 호스팅 및 배포 지원</li>
            </ul>
        </SectionBox>

        <SectionBox>
            <h3><FiAlertCircle /> 제3조 (이용자 의무 및 안전 수칙)</h3>
            <p>나니아랩은 상호 존중하는 교육 환경을 위해 다음과 같은 안전 수칙을 운영합니다.</p>
            <ol>
                <li><strong>클린 채팅 준수</strong>: 비속어, 욕설 등 타인에게 불쾌감을 주는 언어 사용 시 세이프티 가드에 의해 이용이 제한될 수 있습니다.</li>
                <li><strong>시스템 보안 유지</strong>: 서비스의 취약점을 악용하거나 비정상적인 방법으로 서버에 부하를 주는 행위를 금지합니다.</li>
            </ol>
        </SectionBox>

        <SectionBox>
            <h3><FiFileText /> 제4조 (저작권 규정)</h3>
            <p>회원은 자신이 직접 창작한 결과물에 대해 <strong>회사와 공동으로</strong> 권리를 가집니다.</p>
            <ol>
                <li>이용자가 작성한 코드 및 프로젝트의 저작권은 <strong>이용자 본인 및 나니아랩</strong>에 귀속됩니다.</li>
                <li>회사는 우수 사례 홍보, 교육적 연구 및 서비스 개선을 위해 이용자의 창작물을 활용할 수 있습니다.</li>
            </ol>
        </SectionBox>

        <p className="text-center text-slate-500 text-sm mt-4">
            공고 일자: 2025년 12월 3일 | <strong>나니아랩 (NARNIA LAB)</strong>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button 
            variant="primary" 
            style={{ 
                borderRadius: '1rem', 
                fontWeight: 800, 
                padding: '0.75rem 2.5rem',
                background: '#8b5cf6',
                border: 'none',
                boxShadow: '0 10px 20px rgba(139, 92, 246, 0.3)'
            }} 
            onClick={onHide}
        >
            확인 및 닫기
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default TermsModal;
